<template>
  <td
    v-if="visible"
    class="text-breakout"
    :class="rootClasses"
    :data-label="label"
    :style="columnWidth"
    v-bind="$attrs"
  >
    <slot />
  </td>
</template>

<script>
import {
  defineComponent,
  ref,
  computed,
  onBeforeMount,
  onBeforeUnmount,
  inject,
} from 'vue'

export default defineComponent({
  /**
   * @type {String}
   * @description This is the name of the component this will also
   * be the tag name of the component to use in templates
   * @example
   * <ak-tablecolumn label="Name" field="name" />
   */
  name: 'AkTableColumn',

  /**
   * @type {Object}
   * @description A collection of properties that may be passed into
   * the component these will be bound to the component instance and
   * may be access in the template and component methods.
   */
  props: {
    /**
     * @type {String}
     * @description The label to be used for the column
     */
    label: {
      type: String,
      default: undefined,
    },

    /**
     * @type {String|Number}
     * @description Cusom key
     */
    customKey: {
      type: [String, Number],
      default: undefined,
    },

    /**
     * @type {String}
     * @description The field to output from the row
     */
    field: {
      type: String,
      default: undefined,
    },

    /**
     * @type {Mixed}
     * @description Meta data for the column
     */
    meta: {
      type: [String, Number, Boolean, Function, Object, Array],
      default: undefined,
    },

    /**
     * @type {Number|String}
     * @description The width of the column
     */
    width: {
      type: [Number, String],
      default: undefined,
    },

    /**
     * @type {Boolean}
     * @description Determines if the column has a boolean value
     * to display
     */
    numeric: {
      type: Boolean,
      default: false,
    },

    /**
     * @type {Boolean}
     * @description Determines whether to center the content or not
     */
    centered: {
      type: Boolean,
      default: false,
    },

    /**
     * @type {Boolean}
     * @description Determines whether to right align the content or not
     */
    right: {
      type: Boolean,
      default: false,
    },

    /**
     * @type {Boolean}
     * @description Determines if the column is sortable or not
     */
    sortable: {
      type: Boolean,
      default: false,
    },
    headingMinWidth: {
      type: [Number, String],
      default: undefined,
    },
    headerSpacing: {
      type: Boolean,
      default: false,
    },
    /**
     * @type {Boolean}
     * @description Determines if the column is visible or not
     */
    visible: {
      type: Boolean,
      default: true,
    },

    /**
     * @type {Function}
     * @description Custom sort function
     */
    customSort: {
      type: Function,
      default: undefined,
    },

    /**
     * @type {Boolean}
     * @description Makes the cell as small as possible
     */
    mini: {
      type: Boolean,
      default: false,
    },

    /**
     * @type {Boolean}
     * @description Used internaly by the table, this is used when the
     * columns are given to the table as an array and the ak-table-column
     * component is not manually used.
     */
    internal: {
      type: Boolean,
      default: false,
    },

    /**
     * @type {Number}
     * @description Detects if there are multiple sections for a column
     * and makes header span their width
     */
    colSpan: {
      type: Number,
      default: 1,
    },

    /**
     * @type {Boolean}
     * @description Checks if header needs to be capitalized
     */
     capitalize: {
      type: Boolean,
      default: false,
    },
  },

  /**
   * @param {Object} props - The properties of the component
   * @description Sets up the component and returns an object containing all of the
   * attributes for the component to use.
   */
  setup(props) {
    const key = ref(props.customKey || props.label)
    const isTableColumn = ref(true)
    const parent = inject('akTable')

    /**
     * @returns {Object}
     * @description Returns the classes that need to be applied to the
     * root element of the component.
     */
    const rootClasses = computed(() => {
      return {
        'text-right': (props.numeric && !props.centered) || props.right,
        'text-centered': props.centered,
        'ak-table__cell--1': props.mini,
        'text-nowrap text-monospace': props.numeric,
      }
    })

    /**
     * @returns {Object}
     * @description A reactive object containing the styles that determine
     * the column width.
     */
    const columnWidth = computed(() => {
      return {
        width: `${props.width}${Number.isInteger(props.width) ? 'px' : ''}`,
      }
    })

    onBeforeMount(() => {
      if (!parent || (parent && !parent.isTable)) {
        throw new Error('You should wrap akTableColumn on a akTable')
      }

      if (props.internal) return

      // Since we're using scoped prop the columns gonna be multiplied,
      // this finds when to stop based on the newKey property.
      const repeated = parent.columnsModel.some(
        (column) => column.key === key.value
      )

      if (!repeated) {
        parent.columnsModel.push({
          key: key.value,
          label: props.label,
          visible: props.visible,
          headingMinWidth: props.headingMinWidth,
          headerSpacing: props.headerSpacing,
          sortable: props.sortable,
          colSpan: props.colSpan,
          field: props.field,
          internal: props.internal,
          capitalize: props.capitalize,
        })
      }
    })

    onBeforeUnmount(() => {
      if (props.internal) return

      if (parent.columnsModel || parent.columnsModel.length) {
        return
      }

      const index = parent.columnsModel
        .map((column) => column.key)
        .indexOf(key.value)

      if (index >= 0) {
        parent.columnsModel.splice(index, 1)
      }
    })

    return {
      key,
      isTableColumn,
      rootClasses,
      columnWidth,
    }
  },
})
</script>
